import React from 'react';

interface GraphFieldProps {
  label?: string;
  description?: string;
  labels: string[];
  values: number[];
}

const COLORS = [
  'bg-amber-500 dark:bg-amber-400',
  'bg-emerald-500 dark:bg-emerald-400',
  'bg-blue-500 dark:bg-blue-400',
  'bg-rose-500 dark:bg-rose-400',
  'bg-purple-500 dark:bg-purple-400',
  'bg-cyan-500 dark:bg-cyan-400',
  'bg-orange-500 dark:bg-orange-400',
];

const GraphField = ({ label, description, labels, values }: GraphFieldProps) => {
  const data = labels
    .map((l, i) => ({
      label: l,
      value: values?.[i] || 0,
      color: COLORS[i % COLORS.length],
    }))
    .sort((a, b) => b.value - a.value);

  const total = data.reduce((acc, curr) => acc + curr.value, 0);

  return (
    <div className="flex w-full flex-col gap-4 py-3">
      {/* Label & Description */}
      {(label || description) && (
        <div className="min-w-0 flex-1">
          {label && (
            <div className="font-sans text-base font-semibold leading-tight text-primary-700 dark:text-primary-200">
              {label}
            </div>
          )}
          {description && (
            <div className="mt-1 font-sans text-sm leading-snug text-content-500 dark:text-content-400">
              {description}
            </div>
          )}
        </div>
      )}

      {/* Bar / "Suwak" */}
      <div className="flex h-6 w-full overflow-hidden rounded-full bg-content-100 shadow-inner dark:bg-content-800 border border-content-200 dark:border-content-700">
        {total === 0 ? (
          <div className="h-full w-full bg-content-200 dark:bg-content-700" />
        ) : (
          data.map((item, index) => {
            if (item.value === 0) return null;
            const percentage = (item.value / total) * 100;
            return (
              <div
                key={index}
                style={{ width: `${percentage}%` }}
                className={`h-full ${item.color} transition-all duration-500 border-r border-black/10 dark:border-white/10 last:border-r-0`}
                title={`${item.label}: ${item.value}`}
              />
            );
          })
        )}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-x-6 gap-y-2 px-1">
        {data.map((item, index) => (
          <div key={index} className="flex items-center gap-2">
            <div className={`h-3 w-3 rounded-full ${item.color} shadow-sm`} />
            <div className="font-sans text-sm font-medium text-content-700 dark:text-content-300">
              {item.label}{' '}
              <span className="ml-0.5 font-mono text-content-500 dark:text-content-400">
                ({item.value})
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default GraphField;
